import { useIntl } from 'react-intl';

import ConfirmDialog from '../../../../components/dialogs/ConfirmDialog';
import type { AegOccurrenceChatController } from '../../../../hooks/useAegOccurrenceChat';
import { formatDateTimeWithTimezone } from '../../../../utils/dateUtils';
import './styles.css';

type ChatProps = AegOccurrenceChatController & {
    disabled?: boolean;
};

export default function Chat({
    disabled,
    comments,
    loading,
    newCommentText,
    submittingNew,
    editingCommentId,
    editDraft,
    savingEditUuid,
    deletingId,
    showConfirmDialog,
    commentToDelete,
    setNewCommentText,
    setEditDraft,
    submitNewComment,
    beginEdit,
    cancelEdit,
    saveEdit,
    removeComment,
    setCommentToDelete,
    setShowConfirmDialog,
}: ChatProps) {
    const intl = useIntl();
    const t = (id: string) => intl.formatMessage({ id: `intl-msg:${id}` });

    return (
        <div className='aeg-chat display-flex flex-column gap-15'>
            {!loading && !disabled && (
                <div
                    className={`aeg-chat-compose display-flex flex-column gap-10 ${comments.length ? 'has-comments' : ''}`}
                >
                    <label className='display-flex flex-column gap-5 margin-0'>
                        <span className='text-size-12 text-medium text-color-darker'>
                            {t('tas.occurrence.comments.submit-comment')}
                        </span>
                        <textarea
                            className='form-control aeg-chat-textarea'
                            disabled={submittingNew}
                            onChange={event => setNewCommentText(event.target.value)}
                            placeholder={t('tas.occurrence.comments.placeholder')}
                            value={newCommentText}
                        />
                    </label>
                    <div className='display-flex justify-content-end'>
                        <button
                            className={`btn btn-primary ${submittingNew ? 'btn-loading disabled' : ''}`}
                            disabled={submittingNew || !newCommentText.trim()}
                            onClick={() => void submitNewComment()}
                            type='button'
                        >
                            {t('tas.occurrence.comments.submit-comment')}
                        </button>
                    </div>
                </div>
            )}
            {loading ? (
                <div className='aeg-chat-empty display-flex align-items-center justify-content-center padding-30 text-color-dark'>
                    <span className='spinner margin-right-10'>
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                        <span />
                    </span>
                    {t('tas.occurrence.comments.loading')}
                </div>
            ) : (
                <ul className='aeg-chat-messages margin-0 padding-0 display-flex flex-column gap-10'>
                    {comments.length ? (
                        comments.map(comment => {
                            const isEditing = editingCommentId === comment.id;
                            const meta = [
                                comment.commentedByDescription,
                                comment.createdAt ? formatDateTimeWithTimezone(comment.createdAt) : '',
                            ]
                                .filter(Boolean)
                                .join(' · ');

                            return (
                                <li
                                    className={`aeg-chat-message padding-10 border rounded ${
                                        comment.availableToEdit ? 'me' : 'bg-lightest'
                                    }`}
                                    key={comment.id}
                                >
                                    <div className='aeg-chat-header margin-bottom-5'>
                                        <div className='display-flex align-items-baseline flex-wrap gap-10'>
                                            <strong className='aeg-chat-author text-color-darkest'>
                                                {comment.commentedBy ?? '—'}
                                            </strong>
                                            <small className='text-color-dark'>{meta}</small>
                                            {comment.wasUpdated && (
                                                <small className='text-bold text-color-info'>
                                                    {t('tas.occurrence.comments.edited')}
                                                </small>
                                            )}
                                        </div>
                                        {comment.availableToEdit && !isEditing && !disabled && (
                                            <div className='aeg-chat-actions'>
                                                <button
                                                    aria-label={t('tas.occurrence.comments.edit')}
                                                    className='aeg-chat-icon-button'
                                                    onClick={() => beginEdit(comment)}
                                                    type='button'
                                                >
                                                    <span className='rioglyph rioglyph-pencil' />
                                                </button>
                                                <button
                                                    aria-label={t('tas.occurrence.comments.delete')}
                                                    className='aeg-chat-icon-button'
                                                    disabled={deletingId === comment.id}
                                                    onClick={() => {
                                                        setCommentToDelete(comment);
                                                        setShowConfirmDialog(true);
                                                    }}
                                                    type='button'
                                                >
                                                    <span className='rioglyph rioglyph-trash' />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {isEditing && !disabled ? (
                                        <div className='display-flex flex-column gap-10'>
                                            <textarea
                                                aria-label={t('tas.occurrence.comments.edit')}
                                                className='form-control aeg-chat-textarea'
                                                disabled={savingEditUuid === comment.id}
                                                onChange={event => setEditDraft(event.target.value)}
                                                value={editDraft}
                                            />
                                            <div className='display-flex justify-content-end align-items-center gap-10'>
                                                <button
                                                    className='btn btn-link'
                                                    disabled={savingEditUuid === comment.id}
                                                    onClick={cancelEdit}
                                                    type='button'
                                                >
                                                    {t('tas.occurrence.comments.cancel')}
                                                </button>
                                                <button
                                                    className={`btn btn-primary ${
                                                        savingEditUuid === comment.id ? 'btn-loading disabled' : ''
                                                    }`}
                                                    disabled={savingEditUuid === comment.id || !editDraft.trim()}
                                                    onClick={() => void saveEdit()}
                                                    type='button'
                                                >
                                                    {t('tas.occurrence.comments.save')}
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className='aeg-chat-text text-color-darkest'>{comment.comment}</div>
                                    )}
                                </li>
                            );
                        })
                    ) : (
                        <li className='aeg-chat-empty padding-30 border border-style-dashed rounded text-center text-color-dark'>
                            {t('support.aeg.detail.chat.empty')}
                        </li>
                    )}
                </ul>
            )}
            <ConfirmDialog
                body={t('tas.occurrence.comments.confirm-delete-body')}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={() => {
                    if (commentToDelete) {
                        void removeComment(commentToDelete.id);
                        setShowConfirmDialog(false);
                        setCommentToDelete(null);
                    }
                }}
                show={showConfirmDialog}
                title={t('tas.occurrence.comments.confirm-delete-title')}
            />
        </div>
    );
}
